import { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';

const inputCls =
  'border border-[#D9D5EE] rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#7047FF] focus:ring-2 focus:ring-[#7047FF]/20';

const STATUSES = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'closed', label: 'Closed' },
];

const badgeCls = {
  new: 'bg-[#C7F464] text-[#17142A]',
  contacted: 'bg-[#EFEBFF] text-[#7047FF]',
  closed: 'bg-gray-100 text-gray-500',
};

export default function EnterprisePanel() {
  // ---- alert numbers ----
  const [list, setList] = useState([]);
  const [label, setLabel] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');

  // ---- enquiries ----
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [openId, setOpenId] = useState(null);

  const loadNumbers = () => {
    api.get('/admin/enterprise/numbers').then((r) => setList(r.data)).catch(() => {});
  };

  const loadInquiries = useCallback(async (p) => {
    setLoading(true);
    setListError('');
    try {
      const res = await api.get('/admin/enterprise/inquiries', { params: { page: p } });
      const d = res.data || {};
      setRows(Array.isArray(d.data) ? d.data : []);
      setLastPage(d.last_page || 1);
      setTotal(d.total ?? (d.data ? d.data.length : 0));
    } catch (err) {
      setListError(err.response?.data?.message || 'Could not load enterprise enquiries.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadNumbers(); }, []);
  useEffect(() => { loadInquiries(page); }, [page, loadInquiries]);

  const add = async () => {
    setError('');
    const clean = phone.replace(/\D/g, '');
    if (clean.length < 10) { setError('Enter a valid number (with 91 country code)'); return; }
    try {
      await api.post('/admin/enterprise/numbers', { label, phone: clean });
      setLabel(''); setPhone(''); loadNumbers();
    } catch (e) {
      setError(e.response?.data?.message || 'Could not add number');
    }
  };

  const toggle = (id) => api.patch(`/admin/enterprise/numbers/${id}/toggle`).then(loadNumbers);
  const remove = (id) => {
    if (window.confirm('Delete this number?')) {
      api.delete(`/admin/enterprise/numbers/${id}`).then(loadNumbers);
    }
  };

  const changeStatus = async (row, status) => {
    const previous = row.status || 'new';
    setRows((l) => l.map((r) => (r.id === row.id ? { ...r, status } : r)));
    try {
      await api.patch(`/admin/enterprise/inquiries/${row.id}/status`, { status });
    } catch (err) {
      setRows((l) => l.map((r) => (r.id === row.id ? { ...r, status: previous } : r)));
      setListError(err.response?.data?.message || 'Could not update the status.');
    }
  };

  const visible = filter ? rows.filter((r) => (r.status || 'new') === filter) : rows;

  return (
    <div className="space-y-6">
      {/* ---------------- Alert numbers ---------------- */}
      <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 p-5">
        <h3 className="font-bold text-lg text-[#252438] mb-1">Enterprise alert numbers</h3>
        <p className="text-sm text-gray-500 mb-4">
          Every Enterprise enquiry is sent on WhatsApp to all active numbers below.
        </p>

        <div className="flex flex-wrap gap-2 mb-2">
          <input placeholder="Name (optional)" value={label} onChange={(e) => setLabel(e.target.value)} className={inputCls} />
          <input placeholder="91XXXXXXXXXX" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputCls} />
          <button
            type="button"
            onClick={add}
            className="px-5 py-2 rounded-xl bg-[#7047FF] hover:bg-[#5B35E0] text-white font-bold text-sm transition"
          >
            + Add
          </button>
        </div>
        {error && <p className="text-sm text-red-600 mb-2">{error}</p>}
        {list.length === 0 && <p className="text-sm text-gray-400 mt-3">No numbers added yet.</p>}

        {list.map((n) => (
          <div key={n.id} className="flex items-center gap-3 border-b border-[#EFEBFF] py-2 text-sm">
            <span className="flex-1 text-[#252438]">{n.label || 'Unnamed'} — {n.phone}</span>
            <button
              type="button"
              onClick={() => toggle(n.id)}
              className={`px-3 py-1 rounded-full text-xs font-bold ${n.is_active ? 'bg-[#C7F464] text-[#17142A]' : 'bg-gray-100 text-gray-500'}`}
            >
              {n.is_active ? 'Active' : 'Paused'}
            </button>
            <button type="button" onClick={() => remove(n.id)} aria-label="Delete number">🗑</button>
          </div>
        ))}
      </div>

      {/* ---------------- Enquiries ---------------- */}
      <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div>
            <h3 className="text-xl font-bold text-[#252438]">Enterprise enquiries</h3>
            <p className="text-sm text-gray-500 mt-1">
              Requests sent from the Corporate plan form. {total} in total.
            </p>
          </div>

          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className={inputCls}
            aria-label="Filter by status"
          >
            <option value="">All statuses</option>
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>

        {listError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 text-red-600 text-sm px-4 py-3">
            {listError}
          </div>
        )}

        {loading ? (
          <p className="text-sm text-gray-500 py-8 text-center">Loading enquiries...</p>
        ) : visible.length === 0 ? (
          <p className="text-sm text-gray-500 py-8 text-center">No enquiries to show.</p>
        ) : (
          <div className="overflow-x-auto border border-[#EFEBFF] rounded-xl">
            <table className="min-w-full text-sm">
              <thead className="bg-[#F8F8FC] text-left text-[#252438]">
                <tr>
                  <th className="px-4 py-3 font-semibold">Company</th>
                  <th className="px-4 py-3 font-semibold">Contact</th>
                  <th className="px-4 py-3 font-semibold">Hiring needs</th>
                  <th className="px-4 py-3 font-semibold">Message</th>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">Received</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => {
                  const status = r.status || 'new';
                  const isOpen = openId === r.id;
                  return (
                    <tr key={r.id} className="border-t border-[#EFEBFF] align-top">
                      <td className="px-4 py-3">
                        <p className="font-semibold text-[#252438]">{r.company_name}</p>
                        {r.city && <p className="text-xs text-gray-500 mt-0.5">{r.city}</p>}
                      </td>

                      <td className="px-4 py-3">
                        {r.name && <p className="text-[#252438]">{r.name}</p>}
                        <a href={`mailto:${r.email}`} className="block text-[#7047FF] hover:underline break-all">
                          {r.email}
                        </a>
                        <a href={`tel:${r.phone}`} className="block text-gray-600 hover:underline">
                          {r.phone}
                        </a>
                      </td>

                      <td className="px-4 py-3 text-gray-600">
                        <p>Size: {r.company_size || '-'}</p>
                        <p>Jobs / month: {r.hiring_volume || '-'}</p>
                      </td>

                      <td className="px-4 py-3 max-w-xs">
                        {r.message ? (
                          <>
                            <p className={`text-gray-700 break-words ${isOpen ? '' : 'line-clamp-2'}`}>
                              {r.message}
                            </p>
                            {r.message.length > 80 && (
                              <button
                                type="button"
                                onClick={() => setOpenId(isOpen ? null : r.id)}
                                className="text-xs font-semibold text-[#7047FF] hover:underline mt-1"
                              >
                                {isOpen ? 'Show less' : 'Read more'}
                              </button>
                            )}
                          </>
                        ) : (
                          <span className="text-gray-400">No message</span>
                        )}
                      </td>

                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {r.created_at ? new Date(r.created_at).toLocaleString() : '-'}
                      </td>

                      <td className="px-4 py-3">
                        <span className={`inline-block text-[11px] font-bold px-2.5 py-1 rounded-full mb-2 ${badgeCls[status] || badgeCls.closed}`}>
                          {STATUSES.find((s) => s.value === status)?.label || status}
                        </span>
                        <select
                          value={status}
                          onChange={(e) => changeStatus(r, e.target.value)}
                          className="block border border-[#D9D5EE] rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none focus:border-[#7047FF]"
                          aria-label={`Change status for ${r.company_name}`}
                        >
                          {STATUSES.map((s) => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {lastPage > 1 && (
          <div className="flex items-center justify-between mt-4">
            <button
              type="button"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-4 py-2 rounded-lg text-sm font-semibold border border-[#7047FF] text-[#7047FF] disabled:opacity-40"
            >
              Previous
            </button>
            <span className="text-sm text-gray-500">Page {page} of {lastPage}</span>
            <button
              type="button"
              disabled={page >= lastPage || loading}
              onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
              className="px-4 py-2 rounded-lg text-sm font-semibold border border-[#7047FF] text-[#7047FF] disabled:opacity-40"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}